import { useEffect, useState } from 'react'
import { IconAlertTriangle, IconCircleCheckFilled, IconCircleDashed } from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import {
  deriveTemplateFacets,
  taxonomyLabelKey,
  templateAiReadiness,
  type TemplateAiIssue,
  type TemplateFacets,
  type TemplateNodeLike,
} from '@webbuilder/schemas'
import { getAdminTemplateDraftElements } from '../lib/api'
import { useI18n } from '../lib/i18n'

/**
 * Qué le falta a un template para que la IA lo use, mientras se llena la ficha.
 *
 * Una lista de pasos con su avance: los dos obligatorios —categoría y slots—
 * dejan el template fuera de la IA; los demás le ayudan a elegirlo mejor. El
 * lienzo se lee una vez, del borrador; la ficha se relee a cada cambio, así
 * que marcar la categoría tacha su paso en el acto. Es la misma función que
 * usa el catálogo de la IA.
 */
const STEPS: { issue: TemplateAiIssue; required: boolean }[] = [
  { issue: 'category', required: true },
  { issue: 'slots', required: true },
  { issue: 'description', required: false },
  { issue: 'layout', required: false },
  { issue: 'colorScheme', required: false },
]

export function TemplateAiReadiness({ token, templateId, category, description, facets }: {
  token: string
  templateId: string
  category: string
  description: string
  facets: TemplateFacets
}) {
  const { t } = useI18n()
  const [elements, setElements] = useState<TemplateNodeLike[] | null>(null)

  useEffect(() => {
    let alive = true
    setElements(null)
    getAdminTemplateDraftElements(token, templateId)
      .then((next) => { if (alive) setElements(next as TemplateNodeLike[]) })
      .catch(() => { if (alive) setElements([]) })
    return () => { alive = false }
  }, [token, templateId])

  if (!elements) return null
  const readiness = templateAiReadiness({ description, facets: { ...facets, category: category || facets.category }, elements })
  const missing = new Set([...readiness.blocking, ...readiness.advice])
  const blocking = new Set(readiness.blocking)
  const done = STEPS.filter((step) => !missing.has(step.issue)).length
  const detected = deriveTemplateFacets(elements)
  const detectedLabels = [
    ...(detected.columns ? [t(taxonomyLabelKey('columns', detected.columns))] : []),
    ...(detected.media ?? []).map((value) => t(taxonomyLabelKey('media', value))),
  ]

  return (
    <section className={`dash-template-ai${readiness.ready ? ' is-ready' : ' is-blocked'}`} aria-label={t('templateAi.title')}>
      <header className="dash-template-ai-head">
        <span className="dash-template-ai-icon" aria-hidden="true">
          {readiness.ready ? <VelticaMascot outline size={18} /> : <IconAlertTriangle size={16} />}
        </span>
        <div>
          <strong>{t('templateAi.title')}</strong>
          <p>{t(readiness.ready ? 'templateAi.ready' : 'templateAi.notReady')}</p>
        </div>
        <span className="dash-template-ai-count">{t('templateAi.progress', { done, total: STEPS.length })}</span>
      </header>
      <div className="dash-template-ai-bar" aria-hidden="true">
        <span style={{ width: `${(done / STEPS.length) * 100}%` }} />
      </div>
      <ul className="dash-template-ai-steps">
        {STEPS.map((step) => {
          const ok = !missing.has(step.issue)
          // «Obligatorio» sólo si deja el template fuera: unos slots deducidos no.
          const required = step.required && blocking.has(step.issue)
          return (
            <li key={step.issue} className={ok ? 'is-done' : required ? 'is-required' : ''}>
              {ok ? <IconCircleCheckFilled size={15} /> : required ? <IconAlertTriangle size={15} /> : <IconCircleDashed size={15} />}
              <span>{t(`templateAi.step.${step.issue}`)}</span>
              {required ? <em>{t('templateAi.required')}</em> : null}
            </li>
          )
        })}
      </ul>
      {readiness.advice.includes('slots') ? (
        <p className="dash-template-ai-note">{t('templateAi.slotsInferred')}</p>
      ) : null}
      {readiness.unslottedTexts > 0 && !missing.has('slots') ? (
        <p className="dash-template-ai-note">{t('templateAi.issue.unslottedText', { count: readiness.unslottedTexts })}</p>
      ) : null}
      {detectedLabels.length ? (
        <p className="dash-template-ai-note">{t('templateAi.detected', { list: detectedLabels.join(' · ') })}</p>
      ) : null}
    </section>
  )
}
